import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import {
  DEFAULT_PROFIT_SETTINGS,
  profitSettingsFromRow,
  toProfitSettingsColumns,
  type ProfitSettings,
} from '@/lib/profitReport';
import { getCachedFinanceCategories } from '@/lib/offlineDataCache';

const SETTINGS_KEY = 'profit-settings';
const cacheKey = (companyId: string) => `orcamigo_profit_settings_${companyId}`;

function readCache(companyId: string | null): ProfitSettings | undefined {
  if (!companyId) return undefined;
  try {
    const raw = localStorage.getItem(cacheKey(companyId));
    return raw ? profitSettingsFromRow(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(companyId: string, row: Record<string, unknown>) {
  try {
    localStorage.setItem(cacheKey(companyId), JSON.stringify(row));
  } catch {
    // Configuração pequena; sem cache a leitura degrada para os padrões antigos.
  }
}

function clearCache(companyId: string) {
  try {
    localStorage.removeItem(cacheKey(companyId));
  } catch {
    // Sem acesso ao storage, os padrões continuam sendo o fallback em memória.
  }
}

export function useProfitSettings(enabled = true) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [SETTINGS_KEY, companyId],
    enabled: Boolean(user?.id && companyId) && enabled,
    networkMode: 'offlineFirst',
    staleTime: 60_000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => readCache(companyId),
    queryFn: async () => {
      if (!companyId) return DEFAULT_PROFIT_SETTINGS;
      const { data, error } = await supabase
        .from('profit_settings')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      const row = data as unknown as Record<string, unknown> | null;
      if (row) writeCache(companyId, row);
      else clearCache(companyId);
      return profitSettingsFromRow(row);
    },
  });

  const settings = query.data ?? DEFAULT_PROFIT_SETTINGS;
  const save = useMutation({
    networkMode: 'offlineFirst' as const,
    mutationFn: async (next: ProfitSettings) => {
      if (!companyId) throw new Error('company');
      if (!(await checkNetworkStatus())) throw new Error('offline');
      const row = {
        company_id: companyId,
        ...toProfitSettingsColumns(next),
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('profit_settings')
        .upsert(row, { onConflict: 'company_id' });
      if (error) throw error;
      writeCache(companyId, row);
      queryClient.setQueryData([SETTINGS_KEY, companyId], next);
      return next;
    },
    onSuccess: () => toast.success('Definições do lucro atualizadas.'),
    onError: (error: Error) => {
      toast.error(
        error.message === 'offline'
          ? 'As definições do lucro exigem conexão.'
          : 'Não foi possível salvar as definições do lucro.',
      );
    },
  });

  return { ...query, settings, save };
}

/**
 * Leitura deliberadamente sem `useFinanceCategories`: abrir o DRE/relatório
 * nunca semeia as dez categorias padrão (#37).
 */
export function useFinanceCategoriesReadOnly(enabled = true) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const query = useQuery({
    queryKey: ['finance-categories-read-only', companyId],
    enabled: Boolean(user?.id && companyId) && enabled,
    networkMode: 'offlineFirst',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => user?.id ? getCachedFinanceCategories(user.id) ?? undefined : undefined,
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('finance_categories')
        .select('id, name, kind')
        .eq('company_id', companyId)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  return { ...query, categories: query.data ?? [] };
}
